/**
 * How big a frame a single save gets.
 *
 * Split out of `palaceLayout` because it is the exact inverse of a formula that
 * lives in `museumCanvas`, and the two have to be verifiable together: the
 * layout preview page (`card-preview.html` at the repo root) draws real cards
 * at the sizes this file hands out, and `palaceLayout` cannot be loaded by a
 * plain ESM page — its room types reach `toolkit.js`, which imports three.
 *
 * Extensions are explicit here for the same reason.
 */

import { contentText, CARD_MODEL, chromeReserve } from '../museumCanvas.js';
import { PALACE, clamp } from './constants.js';
import { mediaForContent } from './media.js';

// The frame-sizing base type size, in metres. Deliberately close to the
// drawing layer's BODY_TEXT fraction of the eventual median frame height, so
// most cards render near 1:1 scale and fitCard only has to fine-tune.
const SIZING_BODY = 0.065;
const SIZING_HEADER = SIZING_BODY * (CARD_MODEL.HEADER_TEXT / CARD_MODEL.BODY_TEXT);
const SIZING_TEXT = { body: SIZING_BODY, header: SIZING_HEADER };
const CAPACITY_SLACK = 1.35; // frame capacity over raw character count

// The hang band is the floor-to-picture-rail strip (0.75m–3.05m), so no frame
// may be taller than it: a monumental save goes wide rather than up.
export const MIN_FRAME_H = 0.55;
export const MAX_FRAME_H = PALACE.RAIL - PALACE.HANG_LO - 0.1;
export const MIN_FRAME_W = 0.5;
export const MAX_FRAME_W = 3.6;

// A save carrying a still is not sized by its caption. Two reasons: the card
// puts the image in a side column, so the text has only part of the width to
// work with, and a media-only save has no caption at all — sized on its twelve
// characters it would draw the smallest frame in the building for the one thing
// on that wall legible from the far side of the room.
const MEDIA_MIN_CHARS = 200;
const MEDIA_ASPECT_BONUS = 0.45; // lands the whole media band above SPLIT_ASPECT

/**
 * Invert museumCanvas's planeCapacity: given how many characters a save needs,
 * solve for the frame height at a chosen aspect ratio.
 *
 *   capacity = (M·w / (CHAR_EM·body)) · ((M·h − chrome) / (LINE·body))
 *
 * with w = aspect·h, which is the quadratic A·B1·h² − A·B0·h − capacity = 0.
 *
 * Every constant comes from CARD_MODEL rather than a literal. These two
 * formulas were previously kept in step by a comment reading "museumCanvas's
 * ratio", and the drawing layer's chrome has since grown a date row and an
 * engagement row that the old literals knew nothing about — every frame in the
 * building would have been short by about a line and a half.
 */
export const frameSizeFor = (tweet, rand) => {
  const { MARGIN, CHAR_EM, LINE_SPACING, MEDIA_COLUMN } = CARD_MODEL;
  const hasMedia = !!mediaForContent(tweet);

  const raw = Math.max(contentText(tweet).length, hasMedia ? MEDIA_MIN_CHARS : 0);
  const capacity = clamp(raw, 12, 620) * CAPACITY_SLACK;
  const aspect = 0.7 + rand() * 0.85 + (hasMedia ? MEDIA_ASPECT_BONUS : 0);

  // Solve against the width the *text* actually gets. When a still shares the
  // card it takes a column, so a media save's frame has to be wider than its
  // character count alone would ask for or the caption is squeezed into the
  // remainder — the same fudge factor, derived instead of guessed.
  const textAspect = hasMedia ? aspect * (1 - MEDIA_COLUMN) : aspect;

  const A = (MARGIN * textAspect) / (CHAR_EM * SIZING_BODY);
  const B1 = MARGIN / (LINE_SPACING * SIZING_BODY);
  const B0 = chromeReserve(SIZING_TEXT) / (LINE_SPACING * SIZING_BODY);
  let h = (A * B0 + Math.sqrt(A * A * B0 * B0 + 4 * A * B1 * capacity)) / (2 * A * B1);

  h = clamp(h, MIN_FRAME_H, MAX_FRAME_H);
  const w = clamp(aspect * h, MIN_FRAME_W, MAX_FRAME_W);
  return { w, h, hasMedia };
};
